import { SocialMediaType } from 'api/graphql-global-types';
import {
  GetStoreBySlug_store_socialMedia,
  GetStoreBySlug_store_storeDetails,
} from 'api/store/types/GetStoreBySlug';

type StoreDetailsWithSocialLinks = GetStoreBySlug_store_storeDetails & {
  facebookLink?: string | null;
  instagramLink?: string | null;
  tiktokLink?: string | null;
  twitterLink?: string | null;
  youtubeLink?: string | null;
};

/**
 * Mapping of storeDetails field names to SocialMediaType
 */
const SOCIAL_MEDIA_FIELD_MAP: Record<
  keyof Pick<
    StoreDetailsWithSocialLinks,
    | 'facebookLink'
    | 'instagramLink'
    | 'tiktokLink'
    | 'twitterLink'
    | 'youtubeLink'
  >,
  SocialMediaType
> = {
  facebookLink: SocialMediaType.Facebook,
  instagramLink: SocialMediaType.Instagram,
  tiktokLink: SocialMediaType.Tiktok,
  twitterLink: SocialMediaType.Twitter,
  youtubeLink: SocialMediaType.Youtube,
} as const;

/**
 * Combines social media links from both socialMedia array and storeDetails fields.
 * Prioritizes entries from socialMedia array to avoid duplicates.
 *
 * @param socialMedia - Array of social media from social_media table
 * @param storeDetails - StoreDetails object with individual social media link fields
 * @returns Combined array of social media links without duplicates
 */
export const combineSocialMediaLinks = (
  socialMedia: GetStoreBySlug_store_socialMedia[],
  storeDetails?: GetStoreBySlug_store_storeDetails | null
): GetStoreBySlug_store_socialMedia[] => {
  if (!storeDetails) {
    return [...socialMedia];
  }

  // Using type assertion since TypeScript types haven't been regenerated yet
  const details = storeDetails as StoreDetailsWithSocialLinks;

  // Create a Set of existing types for O(1) lookup
  const existingTypes = new Set(socialMedia.map((sm) => sm.type));

  // Convert storeDetails link fields to socialMedia format
  const storeDetailsLinks: GetStoreBySlug_store_socialMedia[] = [];

  for (const [fieldName, type] of Object.entries(
    SOCIAL_MEDIA_FIELD_MAP
  ) as Array<[keyof typeof SOCIAL_MEDIA_FIELD_MAP, SocialMediaType]>) {
    // Skip if type already exists in socialMedia array
    if (existingTypes.has(type)) {
      continue;
    }

    const url = details[fieldName];
    // Only add if URL exists and is not empty
    if (url && url.trim() !== '') {
      storeDetailsLinks.push({
        __typename: 'SocialMedia',
        url,
        type,
      });
    }
  }

  return [...socialMedia, ...storeDetailsLinks];
};
